/**
 * web-ui chrome strings — RU-default + EN mirror. Voice is formal-but-warm, no hype
 * words (`agents/shared/reports/product/brand-voice.md`, fleet-internal; not in this
 * repo). App-local for now; consolidation into @agnivo/i18n as a `webUi`
 * namespace is a banked follow-up (the package's Messages shape is landing-specific).
 * These are UI chrome, not locked marketing surfaces — still keep voice discipline.
 */
import { telegramLaunchUserLanguage } from './lib/telegramLaunchLocale';
/**
 * `Locale` → the BCP-47 tag to hand `Intl` / `toLocale*String`.
 *
 * Lives here, next to `Locale` itself, because it is a property OF the locale set: adding a third
 * locale must be one edit, and a `Record<Locale, string>` makes the compiler say so. It was private
 * to `lib/messageTime.ts` until 2026-09-01 and is exported (not copied) for the entitlement reset
 * clock — a second copy would be the same drift trap this file's header already records for
 * `UploadOutcome`, and that `copy.ts` hit for `Locale` itself during S7c-1.
 */
export const INTL_LOCALE = { ru: 'ru-RU', en: 'en-US' };
const ru = {
    appName: 'Agnivo',
    nav: { chat: 'Чат', agents: 'Агенты', settings: 'Настройки' },
    routeFallback: 'Загрузка…',
    chat: {
        emptyTitle: 'Начните диалог',
        emptyBody: 'Выберите агента и напишите сообщение — как в Telegram.',
        placeholder: 'Сообщение…',
        send: 'Отправить',
        agentTyping: 'Агент отвечает…',
        agentTypingElapsed: 'Агент отвечает… {n} с',
        agentWaitExpectation: 'Обычно ответ занимает пару секунд, но иногда — дольше. Пожалуйста, подождите ещё немного.',
        dropHint: 'Отпустите файл — он станет частью знаний агента',
        uploadBlockedReadOnly: 'Перейдите на веб-вкладку, чтобы добавить файл.',
        attach: 'Прикрепить файл в знания агента',
        uploadingFile: 'Загружаем файл…',
        uploadedToKnowledge: 'Файл добавлен в знания агента.',
        uploadFailed: 'Не получилось загрузить файл. Попробуйте ещё раз.',
        uploadFailedNoPermission: 'Не получилось загрузить файл — у вас нет прав на загрузку файлов здесь.',
        uploadFailedPermanent: 'Не получилось загрузить файл. Попробуйте другой файл или обратитесь в поддержку.',
        uploadUnavailable: 'Загрузка файлов недоступна для этого чата.',
        ingestOutcome: {
            deduplicated: '«{filename}» уже есть в базе знаний — добавлять повторно не нужно.',
            unsupportedType: 'Не получилось извлечь текст из «{filename}» — поддерживаются PDF и DOCX.',
            empty: 'В «{filename}» не нашлось текста для сохранения.',
            extractFailed: 'Не удалось прочитать «{filename}». Попробуйте другой файл.',
            sanitizeBlocked: 'Файл «{filename}» не сохранён — содержит слишком чувствительные данные.',
        },
        retry: 'Повторить',
        loadOlder: 'Показать более ранние сообщения',
        sendFailed: 'Не удалось отправить сообщение. Проверьте соединение и повторите.',
        sendRejected: 'Сообщение не принято. Обновите страницу и попробуйте снова — повторная отправка того же сообщения не поможет.',
        conversationRefreshed: 'Диалог был обновлён. Отправьте сообщение ещё раз.',
        sendNotAddressable: 'Сообщение не принято: этот диалог недоступен для выбранного агента.',
        sessionUnverified: 'Откройте приложение заново — не удалось подтвердить вашу учётную запись.',
        demoBadge: 'Демо-режим',
        readOnlyBadge: 'Только чтение',
        askLabel: 'Спросить',
        selectAgent: 'Выберите агента',
        onboarding: {
            body: 'Теперь у вас два диалога с этим агентом — в Telegram и здесь. Агент помнит каждый диалог по отдельности; рабочая память не пересекается между поверхностями.',
            continueToChat: 'Продолжить в чат',
        },
        footer: {
            linkCta: 'Используете Telegram? Привязать →',
            noTgThread: 'В Telegram этого диалога ещё нет. Напишите боту в Telegram — и он появится здесь.',
            webThread: 'Это ваш веб-диалог с этим агентом.',
            tgReadOnly: 'Диалог начался в Telegram — здесь он только для чтения. Продолжить можно в веб-диалоге с этим агентом.',
            openWebThread: 'Открыть веб-диалог →',
            tgNative: 'Ваш Telegram-диалог с этим агентом.',
        },
        threadTabs: {
            web: 'Веб',
            telegram: 'Telegram',
            webSubLabel: 'Веб-диалог',
            telegramSubLabel: 'Telegram-диалог',
            tablist: 'Диалоги с этим агентом',
            webA11y: 'Веб-диалог',
            telegramA11y: 'Telegram-диалог',
            telegramA11yReadOnly: 'Telegram-диалог (только чтение)',
            telegramPlural: 'Telegram-диалоги',
            telegramA11yReadOnlyPlural: 'Telegram-диалоги (только чтение)',
            pickerRow: 'Telegram · последнее сообщение {date}',
            legacyLabel: 'Общая история — до агентов',
        },
        provenance: {
            heading: 'Материалы, использованные для ответа',
        },
        voice: {
            transcriptToggle: 'Расшифровка',
            transcriptPlaceholder: 'Без расшифровки',
            transcriptPending: 'Получение расшифровки…',
            playbackProgress: 'Прогресс воспроизведения',
            record: 'Записать голосовое сообщение',
            recording: 'Идёт запись…',
            stop: 'Остановить запись',
            discard: 'Удалить запись',
            play: 'Прослушать запись',
            pause: 'Пауза',
            playError: 'Не удалось воспроизвести',
            expired: 'Аудио больше недоступно. Текст ответа выше.',
            send: 'Отправить голосовое',
            sending: 'Отправляется…',
            notSent: 'Не отправлено',
            replyPending: 'Доставлено. Агент пока не ответил — ответ появится в истории.',
            micDenied: 'Нет доступа к микрофону. Разрешите доступ в настройках браузера, чтобы записывать голосом.',
            micError: 'Не удалось записать. Попробуйте ещё раз.',
            fallbackHint: 'Голосовые сообщения — в чате с ботом',
            fallbackHintClose: 'Скрыть подсказку',
        },
    },
    agents: {
        title: 'Агенты',
        create: 'Создать агента',
        empty: 'Пока нет ни одного агента.',
        emptyHint: 'Создайте первого — это займёт пару кликов.',
        loadFailed: 'Не удалось загрузить список агентов.',
        loadFailedHint: 'Попробуем ещё раз автоматически.',
        inactive: 'Выключен',
        openChat: 'Открыть чат',
    },
    create: {
        title: 'Новый агент',
        lead: 'Опишите простыми словами, что должен делать агент — как в Telegram.',
        placeholder: 'Например: отвечает на вопросы по моим конспектам и напоминает о дедлайнах…',
        submit: 'Создать',
        generating: 'Готовим черновик…',
        stageReading: 'Разбираем описание…',
        stageDrafting: 'Составляем черновик…',
        generateErrors: {
            timeout: 'Создание заняло слишком много времени. Попробуйте ещё раз.',
            tryAgain: 'С первого раза не получилось. Попробуйте ещё раз.',
            rephrase: 'Черновик не получился. Попробуйте ещё раз или опишите задачу чуть подробнее.',
            descriptionRequired: 'Опишите, что должен делать агент.',
            unavailable: 'Сервис сейчас недоступен. Попробуйте позже.',
            generic: 'Не получилось создать агента. Попробуйте ещё раз.',
        },
        previewTitle: 'Черновик агента',
        confirm: 'Подтвердить',
        back: 'Изменить описание',
        demoNote: 'Демо-режим: агент создаётся локально, без сохранения на сервере.',
        // ⛔ NO next step, deliberately (product A1, 2026-09-03T04:12Z). The first locked draft ended
        // «Откройте редактор, чтобы исправить» — an imperative pointing at an affordance that exists
        // on no surface: `app-core`'s own shared route table has no agent-detail screen, and (as of
        // this writing) admin-tenant's `AgentDetailScreen` — reachable on the composed web-ui surface
        // at `/agents/:agentId` since it was mounted there — edits ability/tool-allowlist settings,
        // not the raw graph spec this warning is about. So the "no next step" premise still holds for
        // THIS warning specifically (correction 2026-09-20, architect finding — the original text's
        // "no agent-detail screen" claim was false of the composed surface, checked here because a
        // stale premise silently outliving the copy decision it justified is worse than a stale
        // comment). Do not "improve" this by adding a call to action; the absence of one is the
        // honest part, and it tells the author not to go hunting. If admin-tenant later gains a way to
        // fix an unsupported graph spec specifically, re-open this with product.
        warnUnsupportedGraph: 'Агент создан, но пока не сможет работать: текущая конфигурация графа не поддерживается.',
        warnGeneric: 'Агент создан, но пока не сможет работать: подробности скоро появятся.',
    },
    settings: {
        title: 'Настройки',
        account: 'Аккаунт',
        usage: 'Использование',
        messagesUsed: 'Сообщения',
        agentsActive: 'Агенты',
        trialDaysLeft: 'Дней пробного периода осталось',
        signOut: 'Выйти',
        appearance: 'Оформление',
        themeSystem: 'Системная',
        themeLight: 'Светлая',
        themeDark: 'Тёмная',
        themeWarm: 'Тёплая',
        themeDim: 'Приглушённая',
        diagnostics: 'Диагностика',
        probeLead: 'Проверка записи звука: записывает ~1 секунду и проверяет, декодируется ли звук и есть ли в нём сигнал. Всё локально, ничего не отправляется.',
        probeRun: 'Проверить запись звука',
        probeRunning: 'Проверяем…',
        probeOk: 'Запись работает: в файле настоящий звук. Проблема — в воспроизведении, а не в записи.',
        probeSilent: 'Файл корректный, но звука в нём нет: микрофон не передаёт сигнал в запись.',
        probeBroken: 'Файл не декодируется: запись повреждена на уровне платформы.',
        accountContact: {
            heading: 'Проверенный email для системных уведомлений',
            none: 'Не добавлен.',
            register: 'Подтвердить и сохранить',
            registering: 'Сохраняем…',
            notVerifiedHint: 'Email ещё не подтверждён — эта функция станет доступна после подтверждения.',
            verifiedAt: 'Подтверждено {date}',
            revoke: 'Отключить',
            revoking: 'Отключаем…',
            suppressed: 'Доставка отключена. Подтвердите новый email.',
            unknownStatus: 'Статус неизвестен',
            registerRefusedUnverified: 'Не удалось подтвердить email. Войдите ещё раз и повторите.',
        },
    },
    auth: {
        signIn: 'Войти',
        loading: 'Загрузка…',
        signInPrompt: 'Войдите, чтобы продолжить.',
        sessionEndedPrompt: 'Сессия завершена. Войдите снова, чтобы продолжить.',
        signInFailed: 'Не удалось войти. Попробуйте ещё раз.',
        signInFailedRepeat: 'Снова не получилось войти. Код ошибки: {code}.',
        callbackFailed: 'Не удалось завершить вход. Попробуйте войти ещё раз.',
        backHome: 'На главную',
    },
    identityGap: {
        title: 'Продолжите в Telegram',
        body: 'Вход выполнен, но у этого аккаунта нет доступа к агенту. Создать агента и общаться с ним можно в Telegram.',
        cta: 'Открыть в Telegram',
    },
    goalPanel: {
        loadError: 'Не удалось загрузить статус цели',
        expand: 'Подробнее',
        collapse: 'Свернуть',
        empty: {
            heading: 'У вас пока нет активной цели.',
            body: 'Чтобы поставить цель, напишите об этом агенту в чате — он задаст несколько вопросов и предложит план.',
            cta: 'Открыть чат с агентом →',
        },
        disabled: {
            heading: 'Постановка целей недоступна на вашем тарифе.',
            upgradeCta: 'Расширить возможности: /upgrade',
        },
        type: {
            agent: 'Агент выполняет',
            user: 'С поддержкой агента',
        },
        state: {
            draft: 'Черновик',
            draftHint: 'Обсуждение цели с агентом ещё не завершено.',
            active: 'Активна',
            achieved: 'Достигнута',
            impossible: 'Недостижима',
            cancelled: 'Отменена',
            replaced: 'Заменена',
            unknown: '—',
            pausedUser: 'На паузе',
            pausedUserHint: 'Вы поставили цель на паузу.',
            pausedUnconfirmed: 'На паузе',
            pausedUnconfirmedHint: 'Цель поставлена на паузу: не было ответа на несколько проверок подряд. Возобновите её, когда будете готовы.',
            pausedBudgetLimited: 'На паузе: бюджет исчерпан',
            pausedBudgetLimitedHint: 'Достигнут лимит расходов на эту цель.',
            pausedEntitlement: 'На паузе: недоступно на тарифе',
            pausedEntitlementHint: 'Возможности вашего тарифа изменились.',
            pausedCapability: 'На паузе: инструмент недоступен',
            pausedCapabilityHint: 'Агенту сейчас не хватает возможности, нужной для этой цели.',
        },
        evidenceHeading: 'Подтверждение',
        evidenceEmpty: 'Пока нет записей',
        evidenceTruncated: 'Показаны последние записи — полная история: /goal status',
        evidenceMissing: 'Без пояснения',
        milestone: {
            confirm: 'Подтвердить',
            reject: 'Отклонить',
            pending: 'Ожидает вашего подтверждения',
            alreadyAnswered: 'Этот шаг уже подтвердили с другим ответом. Обновляем...',
            confirmedLabel: 'подтверждено',
            rejectedLabel: 'отклонено',
            actionError: 'Не удалось отправить ответ. Попробуйте ещё раз.',
        },
        actions: {
            pause: 'Пауза',
            resume: 'Продолжить',
            cancel: 'Отменить',
            cancelConfirm: {
                body: 'Отменить цель насовсем? Работа по ней остановится, вернуть будет нельзя.',
                confirm: 'Да, отменить',
                back: 'Не отменять',
            },
            actionError: 'Не удалось выполнить действие. Попробуйте ещё раз.',
            accept: 'Принять цель и включить сопровождение',
            acceptStale: 'Цель или условия изменились. Проверьте их ещё раз и примите снова.',
            acceptOperatorChanged: 'Изменился оператор ваших данных для этого бота. Проверьте сведения и примите цель ещё раз.',
            acceptDisclosure: {
                before: 'Пока цель активна, я учитываю ваши сообщения, чтобы отмечать прогресс, напоминаю и делаю шаги в пределах плана и лимитов. После завершения история цели хранится до 180 дней, её можно удалить раньше.',
                operatorLabel: 'Оператор:',
                after: 'Подробнее — в',
                terms: 'условиях',
                privacy: 'политике обработки данных',
                and: 'и',
                end: '.',
            },
            acceptError: 'Не удалось принять цель. Попробуйте ещё раз.',
        },
    },
    errors: {
        generic: 'Что-то пошло не так. Попробуйте ещё раз.',
        agentNotAvailable: {
            deleted: 'Этот агент был удалён. Выберите другого или создайте нового.',
            unauthorized: 'У вас нет доступа к этому агенту.',
            notProvisioned: 'Агент ещё готовится — попробуйте через минуту.',
            fallback: 'Этот агент сейчас недоступен. Выберите другого.',
        },
        agentLimitHit: {
            message: 'Достигнут лимит агентов для вашего тарифа.',
            messageWithLimit: 'Достигнут лимит агентов для вашего тарифа ({max_agents}).',
            cta: 'Обновить тариф',
        },
    },
};
const en = {
    appName: 'Agnivo',
    nav: { chat: 'Chat', agents: 'Agents', settings: 'Settings' },
    routeFallback: 'Loading…',
    chat: {
        emptyTitle: 'Start a conversation',
        emptyBody: 'Pick an agent and write a message — just like in Telegram.',
        placeholder: 'Message…',
        send: 'Send',
        agentTyping: 'Agent is replying…',
        agentTypingElapsed: 'Agent is replying… {n}s',
        agentWaitExpectation: 'This usually takes a few seconds, but sometimes a little longer. Please hold on.',
        dropHint: 'Drop the file — it becomes part of the agent’s knowledge',
        uploadBlockedReadOnly: 'Switch to the web tab to add a file.',
        attach: 'Attach a file to the agent’s knowledge',
        uploadingFile: 'Uploading file…',
        uploadedToKnowledge: 'File added to the agent’s knowledge.',
        uploadFailed: 'Upload failed. Please try again.',
        uploadFailedNoPermission: "Couldn't upload the file — you don't have permission to upload files here.",
        uploadFailedPermanent: 'Upload failed. Try a different file or contact support.',
        uploadUnavailable: "File uploads aren't available for this chat.",
        ingestOutcome: {
            deduplicated: '«{filename}» is already in the knowledge base — no need to add it again.',
            unsupportedType: 'Couldn’t extract text from «{filename}» — only PDF and DOCX are supported.',
            empty: 'No text to save was found in «{filename}».',
            extractFailed: 'Couldn’t read «{filename}». Please try another file.',
            sanitizeBlocked: '«{filename}» was not saved — it contains data too sensitive to store.',
        },
        retry: 'Retry',
        loadOlder: 'Show earlier messages',
        sendFailed: 'Couldn’t send your message. Check your connection and try again.',
        sendRejected: 'The message wasn’t accepted. Reload the page and try again — sending the same message again won’t help.',
        conversationRefreshed: 'This conversation was refreshed. Send your message again.',
        sendNotAddressable: 'Message not accepted: this conversation isn’t available for the selected agent.',
        sessionUnverified: 'Reopen the app — we couldn’t verify your account.',
        demoBadge: 'Demo mode',
        readOnlyBadge: 'Read-only',
        askLabel: 'Ask',
        selectAgent: 'Select an agent',
        onboarding: {
            body: "You now have two conversations with this agent — on Telegram and here. The agent remembers each conversation separately; working memory doesn't carry between surfaces.",
            continueToChat: 'Continue to chat',
        },
        footer: {
            linkCta: 'Use Telegram? Link →',
            noTgThread: "This conversation doesn't exist on Telegram yet. Message the bot there and it appears here.",
            webThread: 'This is your web conversation with this agent.',
            tgReadOnly: 'This conversation started on Telegram — here it is read-only. You can continue in your web conversation with this agent.',
            openWebThread: 'Open web thread →',
            tgNative: 'Your Telegram conversation with this agent.',
        },
        threadTabs: {
            web: 'Web',
            telegram: 'Telegram',
            webSubLabel: 'Web thread',
            telegramSubLabel: 'Telegram thread',
            tablist: 'Threads with this agent',
            webA11y: 'Web thread',
            telegramA11y: 'Telegram thread',
            telegramA11yReadOnly: 'Telegram thread (read-only)',
            telegramPlural: 'Telegram chats',
            telegramA11yReadOnlyPlural: 'Telegram chats (read-only)',
            pickerRow: 'Telegram · last message {date}',
            legacyLabel: 'Shared history — pre-agent',
        },
        provenance: {
            heading: 'Materials used for this answer',
        },
        voice: {
            transcriptToggle: 'Transcript',
            transcriptPlaceholder: 'No transcript',
            transcriptPending: 'Getting transcript…',
            playbackProgress: 'Playback progress',
            record: 'Record a voice message',
            recording: 'Recording…',
            stop: 'Stop recording',
            discard: 'Discard recording',
            play: 'Play recording',
            pause: 'Pause',
            playError: 'Couldn’t play',
            expired: 'Audio is no longer available. The reply text is above.',
            send: 'Send voice message',
            sending: 'Sending…',
            notSent: 'Not sent',
            replyPending: 'Delivered. The agent hasn’t replied yet — the reply will appear in the history.',
            micDenied: 'No microphone access. Allow it in your browser settings to record by voice.',
            micError: 'Couldn’t record. Please try again.',
            fallbackHint: 'Voice messages — in the bot chat',
            fallbackHintClose: 'Hide hint',
        },
    },
    agents: {
        title: 'Agents',
        create: 'Create an agent',
        empty: 'No agents yet.',
        emptyHint: 'Create your first — it takes a couple of clicks.',
        loadFailed: 'Couldn’t load your agents.',
        loadFailedHint: 'We’ll try again automatically.',
        inactive: 'Inactive',
        openChat: 'Open chat',
    },
    create: {
        title: 'New agent',
        lead: 'Describe in plain words what the agent should do — same as in Telegram.',
        placeholder: 'For example: answers questions about my notes and reminds me of deadlines…',
        submit: 'Create',
        generating: 'Preparing a draft…',
        stageReading: 'Reading the description…',
        stageDrafting: 'Writing the draft…',
        generateErrors: {
            timeout: 'Creating the agent took too long. Please try again.',
            tryAgain: "That didn't work the first time. Please try again.",
            rephrase: "We couldn't build a draft. Try again, or describe the task in a bit more detail.",
            descriptionRequired: 'Tell us what the agent should do.',
            unavailable: 'The service is unavailable right now. Please try later.',
            generic: "We couldn't create the agent. Please try again.",
        },
        previewTitle: 'Agent draft',
        confirm: 'Confirm',
        back: 'Edit description',
        demoNote: 'Demo mode: the agent is created locally, nothing is stored on the server.',
        // Mirror of the RU pair — see there for why neither string names a next step.
        warnUnsupportedGraph: "Agent created, but it won't run yet — the current graph configuration isn't supported.",
        warnGeneric: "Agent created, but it won't run yet — details will appear soon.",
    },
    settings: {
        title: 'Settings',
        account: 'Account',
        usage: 'Usage',
        messagesUsed: 'Messages',
        agentsActive: 'Agents',
        trialDaysLeft: 'Trial days left',
        signOut: 'Sign out',
        appearance: 'Appearance',
        themeSystem: 'System',
        themeLight: 'Light',
        themeDark: 'Dark',
        themeWarm: 'Warm',
        themeDim: 'Dim',
        diagnostics: 'Diagnostics',
        probeLead: 'Audio capture check: records ~1 second and verifies the audio decodes and carries a real signal. Fully local, nothing is uploaded.',
        probeRun: 'Check audio capture',
        probeRunning: 'Checking…',
        probeOk: 'Capture works: the file contains real audio. The problem is playback, not recording.',
        probeSilent: 'The file is valid but silent: the microphone feeds no signal into the recording.',
        probeBroken: 'The file does not decode: the recording is broken at the platform level.',
        accountContact: {
            heading: 'Verified email for system notices',
            none: 'Not added.',
            register: 'Confirm and save',
            registering: 'Saving…',
            notVerifiedHint: 'Your email is not yet verified — this becomes available once it is.',
            verifiedAt: 'Verified {date}',
            revoke: 'Disconnect',
            revoking: 'Disconnecting…',
            suppressed: 'Delivery is disabled. Confirm a new email.',
            unknownStatus: 'Unknown status',
            registerRefusedUnverified: 'Could not verify your email. Sign in again and retry.',
        },
    },
    auth: {
        signIn: 'Sign in',
        loading: 'Loading…',
        signInPrompt: 'Sign in to continue.',
        sessionEndedPrompt: 'Your session ended. Sign in again to continue.',
        signInFailed: "Couldn't sign you in. Please try again.",
        signInFailedRepeat: "Still couldn't sign you in. Error code: {code}.",
        callbackFailed: 'Could not complete sign-in. Please try again.',
        backHome: 'Back to home',
    },
    identityGap: {
        title: 'Continue in Telegram',
        body: "You're signed in, but this account doesn't have access to an agent. You can create an agent and chat with it in Telegram.",
        cta: 'Open in Telegram',
    },
    goalPanel: {
        loadError: 'Could not load goal status',
        expand: 'Details',
        collapse: 'Collapse',
        empty: {
            heading: 'No active goal yet.',
            body: "To set one, tell your agent about it in chat — it'll ask a few questions and propose a plan.",
            cta: 'Open chat with agent →',
        },
        disabled: {
            heading: "Goal-setting isn't available on your plan.",
            upgradeCta: 'Unlock it: /upgrade',
        },
        type: {
            agent: 'Agent-executed',
            user: 'With agent support',
        },
        state: {
            draft: 'Draft',
            draftHint: 'Still being worked out with your agent.',
            active: 'Active',
            achieved: 'Achieved',
            impossible: 'Impossible',
            cancelled: 'Cancelled',
            replaced: 'Replaced',
            unknown: '—',
            pausedUser: 'Paused',
            pausedUserHint: 'You paused this goal.',
            pausedUnconfirmed: 'Paused',
            pausedUnconfirmedHint: "Paused: no response to check-ins for a while. Resume whenever you're ready.",
            pausedBudgetLimited: 'Paused: budget reached',
            pausedBudgetLimitedHint: "This goal's spend limit has been reached.",
            pausedEntitlement: 'Paused: not available on your plan',
            pausedEntitlementHint: "Your plan's entitlements changed.",
            pausedCapability: 'Paused: a needed capability is unavailable',
            pausedCapabilityHint: 'Your agent is missing something it needs for this goal right now.',
        },
        evidenceHeading: 'Evidence',
        evidenceEmpty: 'No entries yet',
        evidenceTruncated: 'Showing the most recent entries — full history: /goal status',
        evidenceMissing: 'No details provided',
        milestone: {
            confirm: 'Confirm',
            reject: 'Reject',
            pending: 'Awaiting your confirmation',
            alreadyAnswered: 'This step was already answered differently. Refreshing…',
            confirmedLabel: 'confirmed',
            rejectedLabel: 'rejected',
            actionError: "Couldn't send your answer. Please try again.",
        },
        actions: {
            pause: 'Pause',
            resume: 'Resume',
            cancel: 'Cancel',
            cancelConfirm: {
                body: "Cancel this goal for good? Work on it will stop, and this can't be undone.",
                confirm: 'Yes, cancel',
                back: 'Keep it',
            },
            actionError: "Couldn't do that. Please try again.",
            accept: 'Accept the goal and turn on tracking',
            acceptStale: 'The goal or the terms changed. Review them again and accept once more.',
            acceptOperatorChanged: 'The operator of your data for this bot has changed. Check the details and accept again.',
            acceptDisclosure: {
                before: 'While the goal is active, I take your messages into account to track progress, send reminders, and take steps within the plan and limits. After the goal ends, its history is kept for up to 180 days; you can delete it sooner.',
                operatorLabel: 'Operator:',
                after: 'More in the',
                terms: 'terms',
                privacy: 'privacy policy',
                and: 'and the',
                end: '.',
            },
            acceptError: "Couldn't accept the goal. Please try again.",
        },
    },
    errors: {
        generic: 'Something went wrong. Please try again.',
        agentNotAvailable: {
            deleted: 'This agent has been deleted. Pick another or create a new one.',
            unauthorized: "You don't have access to this agent.",
            notProvisioned: 'The agent is still getting ready — try again in a minute.',
            fallback: "This agent isn't available right now. Pick another.",
        },
        agentLimitHit: {
            message: "You've reached the agent limit for your plan.",
            messageWithLimit: "You've reached your plan's agent limit ({max_agents}).",
            cta: 'Upgrade plan',
        },
    },
};
const dictionaries = { ru, en };
/**
 * Locale resolution mirrors THE USER, not the device — the rule, not a preference
 * (brand-voice «Audience language», Dmitry 2026-06-02: mirror the user's language; his
 * 2026-07-17 mobile screenshot showed EN chrome + RU replies because the
 * webview's navigator.language is the PHONE's system language, not Telegram's).
 * Order: Telegram language_code (the user's actual language) → navigator
 * (non-Telegram surfaces: web-ui, desktop) → RU-first default.
 *
 * ⚠️ The Telegram branch reads the LAUNCH BYTES first (`telegramLaunchUserLanguage()` —
 * `location.hash` / the SDK's own sessionStorage snapshot, parsed exactly the way the SDK
 * parses them), then the SDK global. The global is DERIVED from the same bytes, but it
 * arrives with the async SDK — and mini-app resolves the locale at module-evaluation time,
 * before `bootstrapTelegram()` may land. Reading the bytes directly closes that race
 * without awaiting the CDN; see the reader module for the SDK-mirroring and the
 * unchanged-trust-posture notes. Launch bytes outrank the global so the two can never
 * disagree about precedence, only about timing — and timing is the thing this fixes.
 */
export function detectLocale() {
    if (typeof window !== 'undefined') {
        const tgLang = telegramLaunchUserLanguage() ??
            window.Telegram?.WebApp?.initDataUnsafe?.user
                ?.language_code;
        if (tgLang) {
            if (tgLang.toLowerCase().startsWith('ru'))
                return 'ru';
            if (tgLang.toLowerCase().startsWith('en'))
                return 'en';
            return 'ru'; // RU-first for other Telegram locales (we ship ru/en)
        }
    }
    if (typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('en'))
        return 'en';
    return 'ru'; // RU-first
}
export function getMessages(locale = detectLocale()) {
    return dictionaries[locale];
}
/**
 * The rendered outcome line for a knowledge ingest — the `{filename}` substitution
 * that `t.chat.ingestOutcome` is written for.
 *
 * Lives here, next to the strings, so the key selection and the strings cannot drift
 * apart: a reworded key is a compile error rather than a lookup that silently misses.
 *
 * The parameter is `UploadOutcome` ITSELF (`state/chat.ts`), not a literal union
 * repeating its members — that is what makes the exhaustive switch below a guarantee
 * about the real vocabulary. With a hand-copied union the switch stays exhaustive over
 * its own copy and a sixth outcome errors only at the call site, which is a weaker and
 * differently-located guarantee than this docstring used to claim.
 *
 * What the switch does NOT check is that each case returns the RIGHT sibling key —
 * `case 'empty': return t.chat.ingestOutcome.extractFailed` type-checks. That is the
 * job of the literal-pinning test in `i18n.ingestOutcome.test.ts`.
 */
/**
 * The sentence to show for a save-path warning, selected by `kind`.
 *
 * ## Why a total map with BOTH a degrade arm and a `never`
 *
 * The proto makes this a client obligation rather than a style choice, at the `kind` field:
 * *"a newer server may send a Kind an older renderer does not know … An exhaustive switch must
 * degrade to GENERIC warning rendering — never silently swallow, never error."*
 *
 * Those are two requirements pulling opposite ways and the shape below satisfies both:
 * - every DECLARED member gets its own `case`, and the `default` arm's `never` assignment makes a
 *   future proto's new member a **compile error** — nobody adds a kind without deciding its copy;
 * - that same `default` still **returns** the generic string, so a server ahead of our pin
 *   degrades at runtime instead of rendering `undefined`.
 *
 * A bare `default` would lose the compile-time half; a `never` with no return would lose the
 * runtime half. Same shape ratified for `apps/admin-platform/src/lib/billing.ts` (`web!552`).
 *
 * ⛔ `warning.message` is deliberately not an input. It is a diagnostic — product ruling
 * 2026-08-29, and the proto agrees at the field (*"Not for raw rendering to end users"*). Adding a
 * `message` parameter here is the well-meaning change that breaks the ruling; there is no `code`
 * field to key finer copy off at `@agnivo/proto@0.33.1`.
 *
 * ⚠️ **`KIND_VALIDATION` shares the generic arm on purpose, and it has no producer today.**
 * Measured in `agent-core@origin/dev` `5e9c667`: `compileCheckWarnings` has exactly two
 * construction sites, both `KIND_UNSUPPORTED_GRAPH`; validation findings still throw. Product
 * ruled 2026-09-03 that it shares the fallback rather than rendering nothing — rendering nothing
 * for a kind with no producer would re-introduce, at small scale, the silent-unknown defect
 * `web!552` closed. It gets its own string when a producer exists.
 *
 * ⭐ **Neither string names a next step, and that is a ruling rather than an omission.** The first
 * locked draft ended "Open the editor to fix it"; no graph editor exists on any surface, so it was
 * an imperative to perform an impossible action. Product re-locked both strings without it
 * (2026-09-03T04:12Z, option A1) on the honesty floor for affordance claims: do not say "do X"
 * when there is no X.
 */
export function saveWarningCopy(t, kind) {
    switch (kind) {
        case 'KIND_UNSUPPORTED_GRAPH':
            return t.create.warnUnsupportedGraph;
        case 'KIND_VALIDATION':
        case 'KIND_UNSPECIFIED':
        case 'UNRECOGNIZED':
            return t.create.warnGeneric;
        default: {
            // Compile-time: a new proto member lands here and fails to typecheck.
            // Runtime: still degrades to the generic sentence, per the proto's stated obligation.
            const exhaustive = kind;
            void exhaustive;
            return t.create.warnGeneric;
        }
    }
}
export function ingestOutcomeCopy(t, outcome, filename) {
    const template = (() => {
        switch (outcome) {
            case 'deduplicated':
                return t.chat.ingestOutcome.deduplicated;
            case 'unsupported_type':
                return t.chat.ingestOutcome.unsupportedType;
            case 'empty':
                return t.chat.ingestOutcome.empty;
            case 'extract_failed':
                return t.chat.ingestOutcome.extractFailed;
            case 'sanitize_blocked':
                return t.chat.ingestOutcome.sanitizeBlocked;
        }
    })();
    // FUNCTION replacement, not a string one. With a string replacement
    // `String.prototype.replace` expands `$&`, `` $` ``, `$'` and `$$` IN THE
    // REPLACEMENT — and the replacement here is a user-supplied filename, so
    // `q$&w.pdf` renders as «q{filename}w.pdf»: the placeholder leaks back into the
    // one line whose whole job is naming the file. A function replacement is taken
    // literally. (Not a security issue — React escapes the text child — but a silent
    // garbling.) The `{n}` sites this pattern was copied from all substitute
    // `String(<number>)`, which cannot contain `$`; this is the codebase's first
    // interpolation of user-controlled text, so the precedent's shape transferred and
    // its condition did not. Found by umnyi on web!306.
    return template.replace('{filename}', () => filename);
}
//# sourceMappingURL=i18n.js.map